/**
 * Executes the `<script>` elements a COU template lands in the DOM.
 *
 * `morphInnerHTML` mounts publisher markup through `innerHTML`, and the HTML
 * parser flags every `<script>` it creates that way as "already started", so
 * the node sits in the DOM and never runs. A third-party embed pasted into a
 * template therefore leaves an inert tag and an empty container. Replacing the
 * node with an equivalent one built through `createElement` clears that flag.
 *
 * A COU template re-renders on every offer step, modal open, style change and
 * user-attribute change, so each script has to run exactly once per identity.
 * Two layers track that, both keyed off nodes rather than a marker attribute:
 * `syncAttributes` in `domMorph` strips any attribute the incoming markup does
 * not carry, which would wipe a marker on the next morph.
 *
 * - `executedSignature` remembers, per live script node, the identity it ran
 *   with. A morph mutates that node in place rather than replacing it, so the
 *   entry survives, and a changed `src`, attribute or inline body is what makes
 *   the script run again. It has to be a fresh node either way: assigning `src`
 *   to an already-started script is a no-op.
 * - Per-scope state on the instance's shadow root, which outlives the COU root
 *   element. Closing the modal unmounts that root, so on reopen every script
 *   node is brand new and only this can tell that they already ran.
 *
 * Scope state is matched as a multiset rather than by position, so a
 * conditional block adding or removing a script above one that already ran does
 * not make it run again, while a template carrying the same script twice still
 * runs both.
 *
 * The walk does not descend into a `data-ms-preserve` container, whose subtree
 * the SDK does not manage, nor into a progress region the SDK paints — see
 * `insideUnmanagedSubtree`.
 */
import { isSdkOwnedProgressRegion } from './couProgress';
import { COU_PRESERVE_ATTRIBUTE } from './macroAllowlist';
/**
 * Script types the browser executes. Anything else (`application/json`,
 * `application/ld+json`, a template payload) is data, and recreating it would
 * churn the DOM for nothing.
 */
const EXECUTABLE_SCRIPT_TYPES = new Set([
    '',
    'module',
    'text/javascript',
    'application/javascript',
    'text/ecmascript',
    'application/ecmascript',
    'text/x-javascript',
    'application/x-javascript',
    'text/jscript',
    'text/livescript',
]);
const executedSignature = new WeakMap();
const scopeStates = new WeakMap();
/** Started but unfinished external scripts, mapped to "holds back what follows". */
const loadPending = new WeakMap();
/**
 * The instance's shadow root: unique per SDK instance, so two instances never
 * share state, and stable across modal open/close, so a reopen is not a fresh
 * start. `App` always renders into it, see `SDK.renderApp`.
 */
function stateFor(root) {
    const scope = root.getRootNode();
    let state = scopeStates.get(scope);
    if (!state) {
        state = { history: new Map(), inFlight: new Map(), root: null };
        scopeStates.set(scope, state);
    }
    return state;
}
function bump(counts, key, by) {
    const next = (counts.get(key) ?? 0) + by;
    if (next > 0) {
        counts.set(key, next);
    }
    else {
        counts.delete(key);
    }
}
function isExecutable(el) {
    const type = (el.getAttribute('type') ?? '').trim().toLowerCase();
    return EXECUTABLE_SCRIPT_TYPES.has(type);
}
/**
 * True when `el` sits inside a `data-ms-preserve` container or an SDK-owned
 * progress region under `root`.
 *
 * That marker means the SDK does not manage the subtree — `domMorph` does not
 * reconcile it, and neither does this walk touch the scripts in it. Both halves
 * of that are needed for the same guarantee: a loader a third-party embed's
 * snippet appends into its container is a live node this module never created,
 * so it carries no recorded identity, and re-creating it would run the embed a
 * second time. Skipping the subtree keeps "each script runs once" true instead
 * of adding a rule that contradicts it.
 *
 * The consequence, deliberate and not a bug: a `<script>` the *template* writes
 * inside a preserved container never runs at all, because nothing in there is
 * ours to start. A loader must be created from outside the container and paint
 * into it from there, as the example in `docs/custom-offer-unit.md` does.
 *
 * An SDK-owned progress region is skipped for a related reason: its
 * children are copies of the template's single progress unit, written by
 * `couProgress` after the morph, not template markup. The unit's own `<script>`
 * already ran on the render that first wrote it, before the region was claimed;
 * walking the copies would run it again once per repeat, on whichever later
 * render happened to come next. `domMorph` skips the same region.
 *
 * Bounded at `root` so only the template's own markup can preserve anything;
 * the SDK chrome above the COU root cannot.
 */
function insideUnmanagedSubtree(el, root) {
    let node = el.parentElement;
    while (node && node !== root) {
        if (node.hasAttribute(COU_PRESERVE_ATTRIBUTE) ||
            isSdkOwnedProgressRegion(node)) {
            return true;
        }
        node = node.parentElement;
    }
    return false;
}
/**
 * Identity of a script: every attribute plus the inline body. Attributes count
 * because loaders are routinely configured through `data-*`, so a macro moving
 * one produces a different script rather than the same one again.
 */
function signatureOf(el) {
    const pairs = [];
    const attrs = el.attributes;
    for (let i = 0; i < attrs.length; i++) {
        const attr = attrs.item(i);
        /** Read through the IDL property instead, see `activate`. */
        if (attr && attr.name !== 'nonce') {
            pairs.push(`${attr.name}=${attr.value}`);
        }
    }
    pairs.sort();
    /** Serialised, not concatenated: an attribute value cannot forge a pair. */
    return JSON.stringify([pairs, el.nonce, el.textContent ?? '']);
}
/**
 * Swap an inert script for an equivalent live one in the same DOM position and
 * return the replacement, which is the node every later pass will see.
 */
function activate(el, signature, state) {
    const parent = el.parentNode;
    if (!parent) {
        return el;
    }
    const fresh = el.ownerDocument.createElement('script');
    const attrs = el.attributes;
    for (let i = 0; i < attrs.length; i++) {
        const attr = attrs.item(i);
        if (attr && attr.name !== 'nonce') {
            fresh.setAttribute(attr.name, attr.value);
        }
    }
    /**
     * Under a nonce-based CSP the browser hides the `nonce` content attribute
     * once the element is parsed, so copying it through `getAttribute` yields an
     * empty string and the replacement is blocked. The IDL property still carries
     * the real value, and assigning it restores both.
     */
    if (el.nonce) {
        fresh.nonce = el.nonce;
    }
    fresh.text = el.textContent ?? '';
    executedSignature.set(fresh, signature);
    if (el.hasAttribute('src')) {
        /**
         * A DOM-inserted external script is force-async, so nothing orders it
         * against the scripts around it. Setting the property (not the attribute)
         * clears that flag and moves it into the browser's execute-in-order list.
         * A template that asked for `async` keeps its own semantics and holds
         * nothing back, but is still only recorded once it has settled.
         */
        const blocks = !el.hasAttribute('async');
        if (blocks) {
            fresh.async = false;
        }
        loadPending.set(fresh, blocks);
        bump(state.inFlight, signature, 1);
        const settled = () => {
            loadPending.delete(fresh);
            bump(state.inFlight, signature, -1);
            /**
             * Recorded here rather than only in the walk: the mount that started
             * this script may already be gone, and the browser runs it anyway, so
             * the next mount has to see it as executed. A failed load counts as
             * settled for the same reason the parser moves on from one.
             */
            state.history.set(signature, Math.max(state.history.get(signature) ?? 0, 1));
            if (state.root?.isConnected) {
                rehydrateScripts(state.root);
            }
        };
        fresh.addEventListener('load', settled, { once: true });
        fresh.addEventListener('error', settled, { once: true });
    }
    parent.replaceChild(fresh, el);
    return fresh;
}
/**
 * Runs any not-yet-executed `<script>` in `root`, in document order, and leaves
 * every other node untouched. Call after each write of template markup into
 * `root`; it is idempotent.
 *
 * An external script holds back the ones after it, the way the parser would:
 * the walk stops there and resumes from the top when the script settles.
 * Without that, an inline script would run before the `src` script above it had
 * defined the globals it calls. Skipping a preserved subtree does not disturb
 * that: a skipped script is simply not one of ours, so it neither holds anything
 * back nor lets a later script jump an external one it used to wait for.
 */
export function rehydrateScripts(root) {
    /**
     * A detached root cannot run a script anyway, and its root node is not the
     * instance's shadow root: bail rather than record against the wrong scope.
     */
    if (!root.isConnected) {
        return;
    }
    const state = stateFor(root);
    state.root = root;
    const unclaimed = new Map(state.history);
    const unclaimedInFlight = new Map(state.inFlight);
    const ran = new Map();
    const scripts = root.querySelectorAll('script');
    for (let i = 0; i < scripts.length; i++) {
        const el = scripts.item(i);
        /** A script an earlier one in this pass removed is not ours to run. */
        if (!el || !isExecutable(el) || !root.contains(el)) {
            continue;
        }
        /** Nor is anything inside a subtree the morph does not manage either. */
        if (insideUnmanagedSubtree(el, root)) {
            continue;
        }
        const signature = signatureOf(el);
        const ranWith = executedSignature.get(el);
        const claimable = unclaimed.get(signature) ?? 0;
        const fetching = unclaimedInFlight.get(signature) ?? 0;
        /** null once the script's outcome is known; otherwise "holds back what follows". */
        let pending = null;
        if (ranWith === signature) {
            /**
             * Same node, same identity: already running or run on this mount. Still
             * claims its history entry so a second, identical node cannot adopt it.
             */
            if (claimable > 0) {
                unclaimed.set(signature, claimable - 1);
            }
            pending = loadPending.get(el) ?? null;
        }
        else if (ranWith === undefined && claimable > 0) {
            /**
             * Fresh node from a remount (modal reopen) of a script this scope has
             * already run: adopt it instead of running it a second time.
             */
            unclaimed.set(signature, claimable - 1);
            executedSignature.set(el, signature);
        }
        else if (ranWith === undefined && fetching > 0) {
            /**
             * An equivalent script started by a mount that has since been torn down
             * is still fetching, and the browser will run it regardless. Wait for it
             * rather than fetch a second copy, which is what would stack duplicate
             * third-party units.
             */
            unclaimedInFlight.set(signature, fetching - 1);
            pending = !el.hasAttribute('async');
        }
        else {
            pending = loadPending.get(activate(el, signature, state)) ?? null;
        }
        if (pending === null) {
            ran.set(signature, (ran.get(signature) ?? 0) + 1);
            continue;
        }
        if (pending) {
            break;
        }
    }
    /** Highest count seen, not this pass's count: the history only grows. */
    ran.forEach((count, signature) => {
        state.history.set(signature, Math.max(state.history.get(signature) ?? 0, count));
    });
}
